import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { useState } from "react";
import {
  UserIcon,
  BanknotesIcon,
  Bars3Icon,
  XMarkIcon,
  ClipboardDocumentListIcon,
  ArrowLeftStartOnRectangleIcon,
  Cog6ToothIcon,
  ShoppingBagIcon,
} from "@heroicons/react/24/outline";

const UserLayout = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { getItemCount } = useCart();
  const cartItemCount = getItemCount();

  const isActive = path => location.pathname.startsWith(path);

  const navLinks = [
    { path: "/", label: "HOME" },
    { path: "/products", label: "SHOP" },
    { path: "/about", label: "ABOUT" },
    { path: "/rkicha-home", label: "RKICHA HOME" },
    { path: "/contact", label: "CONTACT" },
  ];

  return (
    <div className="min-h-screen bg-info font-sans flex flex-col tracking-wider">
      {/* Header */}
      <header className="bg-white border-b border-primary/20 sticky top-0 z-50">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-20">
            {/* Mobile menu button */}
            <div className="md:hidden flex items-center space-x-4">
              <button
                className="p-1 text-secondary"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
              >
                {isMenuOpen ? (
                  <XMarkIcon className="w-6 h-6" />
                ) : (
                  <Bars3Icon className="w-6 h-6" />
                )}
              </button>
            </div>
            {/* Logo */}
            <div
              onClick={() => navigate("/")}
              className="flex items-center cursor-pointer"
            >
              <div className="max-w-xs m-auto md:m-0">
                <img src="/images/logo.png" alt="RKICHA Logo" className="w-24 md:w-40 b-6 opacity-80" />
              </div>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-10">
              {navLinks.map(link => (
                <button
                  key={link.path}
                  onClick={() => navigate(link.path)}
                  className={`text-sm font-bold transition-colors hover:text-secondary-dark ${
                    isActive(link.path) && link.path !== "/"
                      ? "text-secondary"
                      : "text-secondary/70"
                  }`}
                >
                  {link.label}
                </button>
              ))}
            </nav>

            {/* Right side */}
            <div className="flex items-center space-x-2">
              {/* User Account */}
              <div className="relative group">
                <button
                  onClick={() => setProfileMenuOpen(!profileMenuOpen)}
                  onBlur={() => setTimeout(() => setProfileMenuOpen(false), 200)}
                  className="md:w-10 md:h-10 w-8 h-8 rounded-xl bg-secondary flex items-center justify-center shadow-lg border-2 border-secondary/20 hover:scale-105 transition-all"
                >
                  <span className="text-white text-xs font-black">
                    {user?.firstname?.charAt(0)?.toUpperCase()}
                  </span>
                </button>
                <div
                  className={`absolute right-0 top-full mt-3 bg-white rounded-sm shadow-xl border border-gray-100 transition-all duration-200 min-w-[200px] py-4 z-50 ${
                    profileMenuOpen
                      ? "opacity-100 visible translate-y-0"
                      : "opacity-0 invisible -translate-y-2"
                  } lg:group-hover:opacity-100 lg:group-hover:visible lg:group-hover:translate-y-0`}
                >
                  <div className="px-4 pb-3 mb-3 border-b border-gray-100">
                    <p className="text-xs font-bold text-secondary truncate">
                      {user?.firstname} {user?.lastname}
                    </p>
                  </div>
                  <button
                    onClick={() => navigate("/profile?tab=profile")}
                    className="flex items-center space-x-3 px-4 py-2 text-[12px] md:text-xs font-bold text-secondary hover:bg-info transition-colors w-full text-left"
                  >
                    <UserIcon className="w-4 h-4" />
                    <span>My Profile</span>
                  </button>
                  {user?.role === "customer" && (
                    <button
                      onClick={() => navigate("/profile?tab=order")}
                      className="flex items-center space-x-3 px-4 py-2 text-[12px] md:text-xs font-bold text-secondary hover:bg-info transition-colors w-full text-left"
                    >
                      <ClipboardDocumentListIcon className="w-4 h-4" />
                      <span>Orders</span>
                    </button>
                  )}
                  <button
                    onClick={() => navigate("/payment-notice")}
                    className="flex items-center space-x-3 px-4 py-2 text-[12px] md:text-xs font-bold text-secondary hover:bg-info transition-colors w-full text-left"
                  >
                    <BanknotesIcon className="w-4 h-4" />
                    <span>Payment Confirmation</span>
                  </button>
                  {user?.role === "admin" && (
                    <button
                      onClick={() => navigate("/admin")}
                      className="flex items-center space-x-3 px-4 py-2 text-[12px] md:text-xs font-bold text-secondary hover:bg-info transition-colors w-full text-left"
                    >
                      <Cog6ToothIcon className="w-4 h-4" />
                      <span>Admin Dashboard</span>
                    </button>
                  )}
                  <button
                    onClick={logout}
                    className="flex items-center space-x-3 px-4 py-2 text-[12px] md:text-xs font-bold text-danger hover:bg-red-50 transition-colors w-full text-left mt-2"
                  >
                    <ArrowLeftStartOnRectangleIcon className="w-4 h-4" />
                    <span>Logout</span>
                  </button>
                </div>
              </div>

              <button
                onClick={() => navigate("/cart")}
                className="text-sm font-bold text-secondary hover:text-secondary-dark transition-colors flex items-center gap-2"
              >
                <ShoppingBagIcon className="w-6 h-6" /> {cartItemCount || 0}
              </button>
            </div>
          </div>

          {/* Mobile Navigation */}
          {isMenuOpen && (
            <div className="md:hidden py-8 border-t border-gray-100 flex flex-col space-y-6 text-center">
              {navLinks.map(link => (
                <button
                  key={link.path}
                  onClick={() => {
                    navigate(link.path);
                    setIsMenuOpen(false);
                  }}
                  className="text-lg font-bold tracking-widest text-secondary uppercase block w-full"
                >
                  {link.label}
                </button>
              ))}
              <button
                onClick={() => {
                  navigate("/profile");
                  setIsMenuOpen(false);
                }}
                className="text-lg font-bold tracking-widest text-secondary uppercase block w-full"
              >
                PROFILE
              </button>
              <button
                onClick={() => {
                  logout();
                  setIsMenuOpen(false);
                }}
                className="text-lg font-bold tracking-widest text-danger uppercase pb-4 block w-full"
              >
                LOGOUT
              </button>
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 container mx-auto px-4 py-12">
        <div className="max-w-6xl mx-auto">
          <Outlet />
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-primary/20 py-12">
        <div className="container mx-auto px-4 text-center text-sm font-bold tracking-widest text-secondary uppercase">
          <p>&copy; 2026 rkicha. all rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default UserLayout;
